import React from 'react';
import { EventEditableRow } from './EventFieldEditor';
import DsButton from '../../common/DsButton';
import EventDetailsFlyerPanel from './EventDetailsFlyerPanel';
import CastingContextQuickEdit from './CastingContextQuickEdit';
import EventDetailsParticipantsPanel from './EventDetailsParticipantsPanel';
import EventDetailsOverviewSidebarPanels from './EventDetailsOverviewSidebarPanels';
import { getEventPerformanceStructureRows, resolvePerformanceStructure } from '../../../utils/eventFormDisplay';

export default function EventDetailsPrimarySections({ view }) {
  const {
    EventVenueDisplay,
    assignedAdminsDisplay,
    canEditEventInfo,
    canEditParticipants,
    canEditTicketSales,
    canViewParticipants,
    canViewTicketSales,
    cancelCompLoading,
    cancelCompSaving,
    conciergeApplication,
    compactHeaderDateLabel,
    compactHeaderSubtitleText,
    eventData,
    eventId,
    eventReadOnlyTitle,
    formatDate,
    formatDateTimeJST,
    formatTicketPrice,
    formatTicketQuantity,
    formatTime,
    genres,
    getPerformanceVenueInfo,
    headerEventDateLabel,
    infoPublishAtLabel,
    isConciergeEvent,
    isEventCancelled,
    isInfoExplicitlyUnpublished,
    isMultiPerformanceEvent,
    isToday,
    mapLinkUrl,
    multiPerformanceRows,
    navigate,
    notificationSilenceLoading,
    notificationSilenceSummary,
    openCancelCompModal,
    openCastingShortcut,
    openChat,
    openNotificationSilenceModal,
    parentEventLabel,
    parentEventLink,
    parsePgIntArray,
    progressOverviewItems,
    shouldShowProgressOverview,
    performanceTypeJapanese,
    renderGenreBadges,
    renderMultilineText,
    selectedMultiPerformance,
    setSelectedMultiPerformanceId,
    showCompactEventHeader,
    subTitleText,
    ticketSites,
    tickets,
    titleText,
    unread,
    vendors,
    venueDisplayName,
    venueId,
    venueLink,
    venueResolvedAddress,
  } = view;

  const selectedPerformanceName =
    selectedMultiPerformance?.performance_name ||
    selectedMultiPerformance?.performanceName ||
    '公演';

  // 進捗3本（Tasks→Casting→Tickets）: 権限のある項目のみ表示（一覧側と同一データ/％計算）
  const headerProgressItems = shouldShowProgressOverview
    ? (progressOverviewItems || []).filter((item) => !item.locked)
    : [];

  // 進捗バーのkey → ページ内の対応セクションのアンカー（見つかった最初のものへスクロール）
  const PROGRESS_SECTION_SELECTORS = {
    task: ['[data-section="event-tasks"]', '#event-task-section'],
    casting: ['[data-section="casting"]', '#casting-section'],
    ticket: ['[data-section="ticket-sales"]', '#ticket-sales-section'],
  };
  const scrollToProgressSection = (key) => {
    const selectors = PROGRESS_SECTION_SELECTORS[key];
    if (!selectors) return;
    const target = selectors
      .map((selector) => document.querySelector(selector))
      .find(Boolean);
    if (!target) return;
    target.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  return (
    <>
          {/* ヘッダー(案L1): 上段=タイトル+操作ボタン、下段=進捗フル幅3列バンド */}
          <div className="event-detail-hero" id="event-overview-section">
          <div className="edc-title-row">
            <div className="title-stack">
              {(headerEventDateLabel || isConciergeEvent) ? (
                <div className="title-date-row">
                  {headerEventDateLabel ? (
                    <p className="event-date-label" aria-label="開催日">
                      {headerEventDateLabel}
                    </p>
                  ) : null}
                  {isConciergeEvent ? (
                    <span className="concierge-badge" aria-label="コンシェルジュイベント">
                      コンシェルジュ
                    </span>
                  ) : null}
                </div>
              ) : null}
              <h2 className="page-title page-title--left">{titleText}</h2>
              {subTitleText && <p className="page-subtitle">{subTitleText}</p>}
            </div>
            <div className="event-detail-header-actions">
              <DsButton
                variant="secondary"
                size="sm"
                className="event-detail-shortcut-action"
                onClick={openChat}
                aria-label="イベントチャットを開く"
              >
                <i className="fa-solid fa-comments" aria-hidden="true"></i>
                <span>チャット</span>
                {unread > 0 && (
                  <span className="unread-pill">{unread > 99 ? '99+' : unread}</span>
                )}
              </DsButton>
              <DsButton
                variant="secondary"
                size="sm"
                className="event-detail-shortcut-action"
                onClick={() => openCastingShortcut('offer')}
              >
                <i className="fa-solid fa-paper-plane" aria-hidden="true"></i>
                <span>オファー</span>
              </DsButton>
              <DsButton
                size="sm"
                className="event-detail-shortcut-action event-detail-shortcut-action--primary"
                onClick={() => openCastingShortcut('contract')}
              >
                <i className="fa-solid fa-file-signature" aria-hidden="true"></i>
                <span>本契約</span>
              </DsButton>
              {isToday && <span className="today-badge" aria-label="本日開催">本日</span>}
            </div>
          </div>
          {headerProgressItems.length > 0 ? (
            <aside className="event-detail-hero__progress" aria-label="進捗サマリー">
              {headerProgressItems.map((item) => {
                const itemComplete = item.percent >= 100;
                const canScroll = Boolean(PROGRESS_SECTION_SELECTORS[item.key]);
                return (
                  <div
                    key={item.key}
                    className={`event-card-progress-item event-detail-hero__progress-item tone-${item.tone}${
                      canScroll ? ' is-clickable' : ''
                    }`}
                    role={canScroll ? 'button' : undefined}
                    tabIndex={canScroll ? 0 : undefined}
                    aria-label={canScroll ? `${item.label}のセクションへ移動` : undefined}
                    onClick={canScroll ? () => scrollToProgressSection(item.key) : undefined}
                    onKeyDown={
                      canScroll
                        ? (event) => {
                            if (event.key === 'Enter' || event.key === ' ') {
                              event.preventDefault();
                              scrollToProgressSection(item.key);
                            }
                          }
                        : undefined
                    }
                  >
                    <div className="event-card-progress-labels">
                      <span className="event-card-progress-label">
                        <i
                          className={`fa-solid ${item.icon} event-card-progress-icon event-card-progress-icon--${item.tone}`}
                          aria-hidden="true"
                        ></i>
                        <span className="event-card-progress-text">{item.label}</span>
                        <span className="event-card-progress-note">{item.note}</span>
                      </span>
                      <span className={`event-card-progress-value event-card-progress-value--${item.tone}`}>
                        {item.percent}%
                      </span>
                    </div>
                    <div className="event-card-progress-track">
                      <div
                        className={`event-card-progress-fill event-card-progress-fill--${item.tone}${
                          itemComplete ? ' is-static' : ''
                        }`}
                        style={{ width: `${item.percent}%` }}
                      />
                    </div>
                  </div>
                );
              })}
            </aside>
          ) : null}
          </div>
          <div
            className={`event-detail-compact-summary${showCompactEventHeader ? ' is-visible' : ''}`}
            aria-hidden={!showCompactEventHeader}
          >
            {compactHeaderDateLabel ? (
              <span className="event-detail-compact-summary__date">{compactHeaderDateLabel}</span>
            ) : null}
            <span className="event-detail-compact-summary__text">
              <span className="event-detail-compact-summary__title">{titleText}</span>
              {compactHeaderSubtitleText ? (
                <span className="event-detail-compact-summary__subtitle">
                  {compactHeaderSubtitleText}
                </span>
              ) : null}
            </span>
          </div>

          {isInfoExplicitlyUnpublished && (
            <div className="event-public-status-banner" role="status" aria-live="polite">
              <div className="event-public-status-icon" aria-hidden="true">
                <i className="fa-solid fa-earth-asia" aria-hidden="true"></i>
              </div>
              <div className="event-public-status-text">
                <div className="event-public-status-head">
                  <span className="event-public-status-badge">外部未公開</span>
                  <span>サイト上ではまだ公開されていません。</span>
                </div>
                <div className="event-public-status-sub">
                  外部未公開のため情報の取り扱いに注意してください。
                </div>
                {infoPublishAtLabel ? (
                  <div className="event-public-status-sub event-public-status-sub--schedule">
                    公開予約: {infoPublishAtLabel}
                  </div>
                ) : null}
              </div>
            </div>
          )}

          {/* 中止情報（上部に表示） */}
          {isEventCancelled && (
            <div className="event-cancelled-stack">
              <div className="event-cancelled-banner" role="status" aria-live="polite">
                <div className="event-cancelled-icon" aria-hidden="true">
                  <i className="fa-solid fa-ban" aria-hidden="true"></i>
                </div>
                <div className="event-cancelled-text">
                  <div className="event-cancelled-head">
                    <span className="event-cancelled-badge">中止</span>
                    <span>
                      このイベントは中止されています（{formatDateTimeJST(eventData.canceled_at)} 中止）。
                    </span>
                  </div>
                  {eventData.cancel_reason ? (
                    <div className="event-cancelled-reason">理由: {eventData.cancel_reason}</div>
                  ) : null}
                </div>
              </div>

              <div className="cancel-comp-cta" role="note" aria-label="中止保証金額の一括設定">
                <div className="cancel-comp-cta-text">
                  <div className="cancel-comp-cta-title">中止保証金額を一括設定</div>
                  <div className="cancel-comp-cta-sub">
                    中止となったイベントの出演者に対する保証金額をまとめて設定します（0 円 / 0% も可）。
                  </div>
                </div>
                <DsButton
                  onClick={openCancelCompModal}
                  disabled={!canEditEventInfo || cancelCompSaving || cancelCompLoading}
                  loading={cancelCompLoading}
                  title={!canEditEventInfo ? eventReadOnlyTitle : undefined}
                >
                  保証金額を一括設定
                </DsButton>
              </div>
            </div>
          )}

          <div className="event-detail-primary-grid" data-testid="event-detail-primary-grid">
            <section
              className="event-panel event-overview-panel"
              id="event-info-section"
              data-testid="event-overview-panel"
            >
            <EventDetailsFlyerPanel view={view} selectedPerformanceName={selectedPerformanceName} />

            <div className="info-panel">
              <div className="panel-heading">
                <div>
                  <h3>イベント情報</h3>
                </div>
                <div className="panel-actions">
                  <DsButton
                    size="sm"
                    className="event-detail-panel-action"
                    onClick={() => navigate(`/admin/workspaces/event-operations/events/${eventId}/edit`)}
                    disabled={!canEditEventInfo}
                    title={!canEditEventInfo ? eventReadOnlyTitle : undefined}
                  >
                    <i className="fa-solid fa-pen-to-square" aria-hidden="true"></i>
                    <span className="event-detail-action-label-sp-hide">修正</span>
                  </DsButton>
                </div>
              </div>
              {isMultiPerformanceEvent ? (
                <div className="multi-performance-overview">
                  <div className="multi-performance-selection">
                    <p className="multi-performance-selection__hint">
                      {selectedMultiPerformance ? (
                        <>
                          上部フライヤー: <strong>{selectedPerformanceName}</strong> を表示中
                        </>
                      ) : (
                        '公演を選択すると、その公演のフライヤーが上部に表示されます。'
                      )}
                    </p>
                    {selectedMultiPerformance ? (
                      <DsButton
                        variant="secondary"
                        size="sm"
                        onClick={() => setSelectedMultiPerformanceId(null)}
                      >
                        <i className="fa-solid fa-xmark" aria-hidden="true"></i>
                        <span>選択を解除（共通に戻す）</span>
                      </DsButton>
                    ) : null}
                  </div>
                  <div className="multi-performance-detail-list">
                    {multiPerformanceRows.map((performance, index) => {
                      const venue = getPerformanceVenueInfo(performance);
                      const selected =
                        String(performance?.id || '') === String(selectedMultiPerformance?.id || '');
                      const hasOwnFlyer = Boolean(performance?.flyer_front_url);
                      const toggleSelection = () =>
                        setSelectedMultiPerformanceId(selected ? null : (performance.id || null));
                      return (
                        <div
                          role="button"
                          tabIndex={0}
                          aria-pressed={selected}
                          className={`multi-performance-detail-item${selected ? ' is-selected' : ''}`}
                          key={performance.id || performance.sort_order || index}
                          onClick={toggleSelection}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter' || e.key === ' ') {
                              e.preventDefault();
                              toggleSelection();
                            }
                          }}
                        >
                          <div className="multi-performance-detail-head">
                            <strong>{performance.performance_name}</strong>
                            <span
                              className={`multi-performance-flyer-flag${hasOwnFlyer ? '' : ' is-none'}`}
                            >
                              {hasOwnFlyer ? '専用フライヤーあり' : '専用フライヤーなし'}
                            </span>
                            {selected ? (
                              <span className="multi-performance-detail-shown">
                                <i className="fa-solid fa-caret-up" aria-hidden="true"></i>
                                上部に表示中
                              </span>
                            ) : null}
                          </div>
                          <span>
                            {formatDate(performance.performance_date)}
                            {' '}
                            {[
                              performance.open_time ? `${formatTime(performance.open_time)} 開場` : '',
                              performance.start_time ? `${formatTime(performance.start_time)} 開演` : '',
                              performance.end_time ? `${formatTime(performance.end_time)} 終演` : '',
                            ].filter(Boolean).join(' / ')}
                          </span>
                          <span className="multi-performance-detail-venue">
                            <EventVenueDisplay
                              venueName={venue.name}
                              venueId={venue.venueId}
                              venueDetailBasePath="/admin/workspaces/event-operations/venues"
                              address={venue.address}
                              mapUrl={venue.mapUrl}
                              fallback="会場未設定"
                              linkClassName="side-link"
                            />
                          </span>
                          {venue.address ? (
                            <span className="multi-performance-detail-address">
                              {venue.address}
                            </span>
                          ) : null}
                          {getEventPerformanceStructureRows(resolvePerformanceStructure(performance, eventData)).map((row) => <span key={row.key}>{row.label}：{row.value}</span>)}
                          {performance.notes ? <span>{performance.notes}</span> : null}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ) : null}
              <table className="event-info-table">
                <tbody>
                  <EventEditableRow field="genre" enabled={canEditEventInfo}>
                    <th>ジャンル</th>
                    <td>{renderGenreBadges(eventData.genre, genres, parsePgIntArray)}</td>
                  </EventEditableRow>
                  <CastingContextQuickEdit key={JSON.stringify(eventData.genre)} eventId={eventId} />
                  {getEventPerformanceStructureRows(eventData, { includeEmpty: true, includePerformances: false }).map((row) => (
                    <EventEditableRow key={row.label} field={row.label === '所要時間' ? 'estimated_duration_text' : 'performance_format_text'} enabled={canEditEventInfo}>
                      <th>{row.label}</th>
                      <td>{row.value}</td>
                    </EventEditableRow>
                  ))}
                  {!isMultiPerformanceEvent ? (
                    <EventEditableRow field="event_date" enabled={canEditEventInfo}>
                      <th>日時</th>
                      <td>{formatDate(eventData.event_date)}</td>
                    </EventEditableRow>
                  ) : null}
                  {isConciergeEvent ? (
                    <tr>
                      <th>コンシェルジュ申込</th>
                      <td className="concierge-application-cell">
                        {conciergeApplication?.artist_id ? (
                          <a href={`/admin/workspaces/artist-management/artists/${conciergeApplication.artist_id}`}>
                            {conciergeApplication.artist_name || conciergeApplication.artist_id}
                          </a>
                        ) : (
                          <span className="concierge-application-missing">申込データなし</span>
                        )}
                        {conciergeApplication?.id ? (
                          <span className="concierge-application-meta">申込ID: {conciergeApplication.id}</span>
                        ) : null}
                        <a href="/admin/workspaces/concierge/dashboard" className="concierge-application-link">
                          申込一覧
                        </a>
                      </td>
                    </tr>
                  ) : null}
                  {!isMultiPerformanceEvent ? (
                    <>
                      <EventEditableRow field="open_time" enabled={canEditEventInfo}>
                        <th>開場</th>
                        <td>{formatTime(eventData.open_time)}</td>
                      </EventEditableRow>
                      <EventEditableRow field="start_time" enabled={canEditEventInfo}>
                        <th>開演</th>
                        <td>{formatTime(eventData.start_time)}</td>
                      </EventEditableRow>
                      <EventEditableRow field="venue" enabled={canEditEventInfo}>
                        <th>会場名</th>
                        <td className="event-venue-cell">
                          <EventVenueDisplay
                            venueName={venueDisplayName}
                            venueId={venueId && venueLink ? venueId : null}
                            venueDetailBasePath="/admin/workspaces/event-operations/venues"
                            address={venueResolvedAddress}
                            mapUrl={mapLinkUrl}
                            fallback="未設定"
                            linkClassName=""
                          />
                        </td>
                      </EventEditableRow>
                      <EventEditableRow field={venueId ? 'venue' : 'prefecture'} enabled={canEditEventInfo}>
                        <th>{(venueLink || venueResolvedAddress) ? '住所' : '開催都道府県'}</th>
                        <td className="event-venue-cell">
                          {venueResolvedAddress || eventData.prefecture || '未設定'}
                          {(venueResolvedAddress || eventData.prefecture) && (
                            <DsButton
                              variant="secondary"
                              size="sm"
                              icon
                              className="copy-action"
                              onClick={() => navigator.clipboard?.writeText(venueResolvedAddress || eventData.prefecture || '')}
                              aria-label="住所をコピー"
                              title="住所をコピー"
                            >
                              <i className="fa-regular fa-clipboard" aria-hidden="true"></i>
                            </DsButton>
                          )}
                        </td>
                      </EventEditableRow>
                    </>
                  ) : null}
                  <EventEditableRow field="performance_flag" enabled={canEditEventInfo}>
                    <th>公演区分</th>
                    <td>
                      {isMultiPerformanceEvent
                        ? 'マルチイベント'
                        : performanceTypeJapanese[eventData.performance_flag] || 'ー'}
                      {!isMultiPerformanceEvent && eventData.performance_flag === 'additional' && parentEventLabel ? (
                        <>
                          {' （'}
                          {parentEventLink ? (
                            <a href={parentEventLink} className="side-link">
                              {parentEventLabel}
                            </a>
                          ) : (
                            parentEventLabel
                          )}
                          {'）'}
                        </>
                      ) : null}
                    </td>
                  </EventEditableRow>
                  <EventEditableRow field="event_end_at" enabled={canEditEventInfo}>
                    <th>イベント終了日時</th>
                    <td>{formatDateTimeJST(eventData.event_end_at) || '未設定'}</td>
                  </EventEditableRow>

                  {!eventData.additional_dates?.length && <EventEditableRow field="additional_dates" enabled={canEditEventInfo}><th>追加日程</th><td>未設定</td></EventEditableRow>}
                  {eventData.additional_dates?.map((d, i) => (
                    <EventEditableRow key={i} field="additional_dates" enabled={canEditEventInfo}>
                      <th>追加日程</th>
                      <td>
                        {formatDate(d.additional_date)}（{d.additional_date_title}）
                        {d.description && ` ：${d.description}`}
                      </td>
                    </EventEditableRow>
                  ))}

                  {canViewParticipants ? (
                    <EventEditableRow field="casts" enabled={canEditParticipants}>
                      <th>出演者</th>
                      <td>
                        {eventData.casts?.map((c, i) => (
                          <div key={c?.id || i}>
                            {c.cast_role ? `${c.cast_role}: ` : ''}
                            {c.artist_id ? (
                              <a href={`/admin/workspaces/artist-management/artists/${c.artist_id}`}>
                                {(c.artist_name || c.cast_name || '').trim() || '（未設定）'}
                              </a>
                            ) : (
                              (c.cast_name || '').trim() || '（未設定）'
                            )}
                          </div>
                        ))}
                      </td>
                    </EventEditableRow>
                  ) : null}

                  <EventEditableRow field="event_overview" enabled={canEditEventInfo}>
                    <th>イベント概要</th>
                    <td>{renderMultilineText(eventData.event_overview)}</td>
                  </EventEditableRow>

                  <EventEditableRow field="program" enabled={canEditEventInfo}>
                    <th>プログラム</th>
                    <td>{renderMultilineText(eventData.program)}</td>
                  </EventEditableRow>

                  {canViewTicketSales && (
                    <EventEditableRow field="tickets" enabled={canEditTicketSales}>
                      <th>チケット種別</th>
                      <td className="ticket-site-cell">
                        {tickets.map((t, idx) => (
                          <div key={`${t.ticket_type || idx}-${idx}`} className="ticket-site-line">
                            <span className="ticket-site-name">{t.ticket_type || `種別${idx + 1}`}</span>
                            <span className="ticket-site-note">{formatTicketPrice(t.price)}</span>
                            <span className="ticket-site-note">{formatTicketQuantity(t.quantity)}</span>
                          </div>
                        ))}
                      </td>
                    </EventEditableRow>
                  )}

                  {canViewTicketSales ? (
                    <EventEditableRow field="ticket_info" enabled={canEditTicketSales}>
                      <th>料金・チケット情報</th>
                      <td>{renderMultilineText(eventData.ticket_info)}</td>
                    </EventEditableRow>
                  ) : null}

                  {canViewTicketSales && (
                    <EventEditableRow field="ticket_sites" enabled={canEditTicketSales}>
                      <th>チケットサイト</th>
                      <td className="ticket-site-cell">
                        {ticketSites.map((site, idx) => (
                          <div key={`${site.site_url || idx}-${idx}`} className="ticket-site-line">
                            {site.site_name ? <span className="ticket-site-name">{site.site_name}</span> : null}
                            {site.site_url ? (
                              <a href={site.site_url} target="_blank" rel="noopener noreferrer" className="ticket-site-link">
                                {site.site_url}
                              </a>
                            ) : null}
                            {!site.site_name && !site.site_url ? <span>—</span> : null}
                          </div>
                        ))}
                      </td>
                    </EventEditableRow>
                  )}

                  {canViewTicketSales && (
                    <EventEditableRow field="vendors" enabled={canEditTicketSales}>
                      <th>業者情報</th>
                      <td className="ticket-site-cell">
                        {vendors.map((v, idx) => (
                          <div key={`${v.vendor_name || idx}-${idx}`} className="ticket-site-line">
                            {v.vendor_name ? <span className="ticket-site-name">{v.vendor_name}</span> : null}
                            {v.request_note ? <span className="ticket-site-note">{v.request_note}</span> : null}
                            {renderMultilineText(v.memo, 'ticket-site-note')}
                            {!v.vendor_name && !v.request_note && !v.memo ? <span>—</span> : null}
                          </div>
                        ))}
                      </td>
                    </EventEditableRow>
                  )}

                  <EventEditableRow field="selected_options" enabled={canEditEventInfo}>
                    <th>オプション</th>
                    <td>
                      {eventData.selected_options?.length ? (
                        <div className="options-container">
                          {eventData.selected_options.map((opt, i) => (
                            <span key={i} className="option-badge">
                              {opt}
                            </span>
                          ))}
                        </div>
                      ) : null}
                    </td>
                  </EventEditableRow>

                  <EventEditableRow field="organizer" enabled={canEditEventInfo}>
                    <th>主催</th>
                    <td>{eventData.organizer}</td>
                  </EventEditableRow>
                  <EventEditableRow field="operator" enabled={canEditEventInfo}>
                    <th>運営</th>
                    <td>{eventData.operator}</td>
                  </EventEditableRow>
                  <EventEditableRow field="assignments" enabled={canEditEventInfo}>
                    <th>担当者</th>
                    <td>{assignedAdminsDisplay}</td>
                  </EventEditableRow>
                </tbody>
              </table>
              {canEditEventInfo ? (
                <div className="notification-silence-card">
                  <div>
                    <p className="notification-silence-card__label">通知サイレント時間</p>
                    <p className="notification-silence-card__summary">{notificationSilenceSummary}</p>
                  </div>
                  <DsButton
                    variant="secondary"
                    size="sm"
                    onClick={openNotificationSilenceModal}
                    disabled={notificationSilenceLoading}
                  >
                    <i className="fa-solid fa-bell-slash" aria-hidden="true"></i>
                    <span>設定</span>
                  </DsButton>
                </div>
              ) : null}
            </div>
            </section>
            <aside className="event-detail-primary-sidebar" aria-label="イベント補助情報">
              <EventDetailsParticipantsPanel view={view} />
              <EventDetailsOverviewSidebarPanels view={view} />
            </aside>
          </div>
    </>
  );
}
